import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import useUser from '../../hooks/useUser';

const POLL_INTERVAL_MS = 2000;

// Forces every covered employee to set a new password. Deliberately two-staged:
// the dry run reports the blast radius, and only a run that has seen that report
// can be armed — this locks people out of their accounts, so it should never be
// one careless click.
function ForcePasswordChangeDialog({ open, onClose }) {
  const { forcePasswordChange, getForcePasswordChangeStatus } = useUser();
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [reason, setReason] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const pollTimer = useRef(null);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  // The rotation runs in the background — the request only hands back a job id,
  // so the dialog follows its progress instead of waiting for the work.
  const pollJob = async jobId => {
    try {
      const job = await getForcePasswordChangeStatus(jobId);
      if (job.status === 'running') {
        setProgress(job);
        pollTimer.current = setTimeout(() => pollJob(jobId), POLL_INTERVAL_MS);
        return;
      }

      stopPolling();
      setProgress(null);
      setIsBusy(false);
      if (job.status === 'failed') {
        toast.error(`Passwort-Zwangswechsel abgebrochen: ${job.errorMessage || 'unbekannter Fehler'}`);
        setResult(job);
        return;
      }
      setResult(job);
      toast.success(`${job.flagged} Passwörter zurückgesetzt, ${job.mailed} Mails verschickt.`);
    } catch (_) {
      // A single failed poll is not the job failing — keep watching.
      pollTimer.current = setTimeout(() => pollJob(jobId), POLL_INTERVAL_MS);
    }
  };

  useEffect(() => stopPolling, []);

  // Reopening the dialog while a rotation is still running picks it back up
  // instead of offering to start a second one.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const job = await getForcePasswordChangeStatus();
        if (cancelled || job?.status !== 'running') return;
        setIsBusy(true);
        setProgress(job);
        pollJob(job.jobId);
      } catch (_) {
        // No job yet (404) — nothing to resume.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    stopPolling();
    setPreview(null);
    setProgress(null);
    setResult(null);
    setReason('');
    setIsBusy(false);
    onClose();
  };

  const runDryRun = async () => {
    setIsBusy(true);
    try {
      setPreview(await forcePasswordChange({ dryRun: true }));
    } catch (_) {
      toast.error('Fehler beim Ermitteln der betroffenen Mitarbeiter');
    } finally {
      setIsBusy(false);
    }
  };

  const runForReal = async () => {
    setIsBusy(true);
    try {
      const job = await forcePasswordChange({ dryRun: false, reason: reason.trim() });
      setProgress(job);
      pollJob(job.jobId);
    } catch (err) {
      setIsBusy(false);
      if (err?.code === 'ROTATION_ALREADY_RUNNING') {
        toast.info('Es läuft bereits ein Passwort-Zwangswechsel.');
        const running = err?.data?.data || err?.data;
        if (running?.jobId) {
          setIsBusy(true);
          setProgress(running);
          pollJob(running.jobId);
        }
        return;
      }
      toast.error('Fehler beim Auslösen des Passwort-Zwangswechsels');
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth='sm' fullWidth>
      <DialogTitle>Passwortwechsel erzwingen</DialogTitle>
      <DialogContent>
        {!result && !progress && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <AlertTitle>Das sperrt Mitarbeiter aus</AlertTitle>
            Alle betroffenen Passwörter werden sofort ungültig. Jeder Betroffene bekommt eine
            E-Mail mit einem Link, um ein neues zu vergeben — ohne diesen Link kommt niemand mehr
            ins Management. Die Anmeldung mit Mitarbeiterkarte an der Kasse läuft weiter.
          </Alert>
        )}

        {progress ? (
          <Box>
            <Typography sx={{ mb: 1 }}>
              {progress.flagged} von {progress.matched} Mitarbeitern bearbeitet,{' '}
              {progress.mailed} Mails verschickt.
            </Typography>
            <LinearProgress
              variant={progress.matched ? 'determinate' : 'indeterminate'}
              value={progress.matched ? (progress.flagged / progress.matched) * 100 : 0}
              sx={{ mb: 2 }}
            />
            <Alert severity='info'>
              Der Wechsel läuft im Hintergrund weiter — das Fenster kann geschlossen werden. Beim
              erneuten Öffnen wird der Fortschritt wieder angezeigt.
            </Alert>
          </Box>
        ) : result ? (
          <Box>
            <Alert severity='success' sx={{ mb: 2 }}>
              {result.flagged} Passwörter zurückgesetzt, {result.mailed} Mails verschickt.
            </Alert>
            {result.withoutEmail?.length > 0 && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                <AlertTitle>Ohne E-Mail-Adresse — bitte persönlich versorgen</AlertTitle>
                {result.withoutEmail.join(', ')}
              </Alert>
            )}
            {result.mailFailed?.length > 0 && (
              <Alert severity='error' sx={{ mb: 2 }}>
                <AlertTitle>Mailversand fehlgeschlagen</AlertTitle>
                {result.mailFailed.join(', ')}
              </Alert>
            )}
            <Alert severity='info'>
              Bereits offene Sitzungen laufen noch bis zu 10 Stunden weiter. Um auch die zu
              beenden, muss <code>SESSIONS_INVALID_BEFORE</code> auf den jetzigen Zeitpunkt
              gesetzt und das Backend neu deployt werden.
            </Alert>
          </Box>
        ) : preview ? (
          <Box>
            <Typography sx={{ mb: 2 }}>
              Betroffen sind <strong>{preview.matched}</strong> von {preview.scanned} aktiven
              Mitarbeitern. Ausgenommen: {preview.excludedRoles?.join(', ') || 'keine Rolle'}.
            </Typography>
            {preview.withoutEmail?.length > 0 && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                <AlertTitle>{preview.withoutEmail.length} davon ohne E-Mail-Adresse</AlertTitle>
                Diese Mitarbeiter bekommen keinen Link und müssen persönlich versorgt werden:{' '}
                {preview.withoutEmail.join(', ')}
              </Alert>
            )}
            <TextField
              label='Grund'
              placeholder='z. B. Verdacht auf geleakte Zugangsdaten'
              fullWidth
              required
              value={reason}
              onChange={e => setReason(e.target.value)}
              helperText='Wird im Audit-Log festgehalten.'
            />
          </Box>
        ) : (
          <Typography>
            Zeigt zuerst an, wen es treffen würde. Es wird nichts verändert, bis der Wechsel
            ausdrücklich ausgelöst wird.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={isBusy}>
          {result ? 'Schließen' : 'Abbrechen'}
        </Button>
        {!result && !progress && !preview && (
          <Button
            variant='contained'
            onClick={runDryRun}
            disabled={isBusy}
            startIcon={isBusy ? <CircularProgress size={16} /> : null}
          >
            Betroffene anzeigen
          </Button>
        )}
        {!result && !progress && preview && (
          <Button
            variant='contained'
            color='error'
            onClick={runForReal}
            disabled={isBusy || !reason.trim() || preview.matched === 0}
            startIcon={isBusy ? <CircularProgress size={16} /> : null}
          >
            {preview.matched} Passwörter jetzt zurücksetzen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ForcePasswordChangeDialog;
